import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import UsageBilling from '../components/UsageBilling';
import PrivacyAuditPanel from '../components/PrivacyAuditPanel';
import OrgMembersPanel from '../components/OrgMembersPanel';
import OrgConsultantsPanel from '../components/OrgConsultantsPanel';
import { useUserProfile } from '../hooks/useUserProfile';

/**
 * Organization Admin Settings page.
 * Only visible to users with role=admin.
 * Shows client-visible settings with edit/save/reset capabilities.
 */
export default function OrgSettings() {
  const { profile } = useUserProfile();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [edits, setEdits] = useState({});

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/org/settings');
      setSettings(data.settings || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  function handleEdit(key, value) {
    setEdits(prev => ({ ...prev, [key]: value }));
    setMessage(null);
  }

  function cancelEdit(key) {
    setEdits(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function handleSave() {
    if (Object.keys(edits).length === 0) return;

    setSaving(true);
    setMessage(null);
    try {
      // Coerce values based on setting type
      const coerced = {};
      for (const [key, val] of Object.entries(edits)) {
        const info = settings[key];
        if (info?.type === 'int') coerced[key] = parseInt(val, 10);
        else if (info?.type === 'float') coerced[key] = parseFloat(val);
        else if (info?.type === 'bool') coerced[key] = val === 'true' || val === true;
        else coerced[key] = val;
      }
      const result = await api.post('/org/settings', coerced);
      setMessage(`Updated ${result.updated_count} setting(s)`);
      setEdits({});
      await fetchSettings();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset(key) {
    if (!confirm(`Reset "${key}" to the global default?`)) return;
    setSaving(true);
    try {
      await api.post('/org/settings/delete', [key]);
      setMessage(`Reset ${key} to default`);
      setEdits(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await fetchSettings();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading-screen">Loading settings…</div>;
  if (error) return <div className="error-banner">Error: {error}</div>;

  // The monthly spending ceiling has its own control in Usage & Billing
  // (next to the month's spend it is measured against) — one editor per value.
  const settingKeys = Object.keys(settings).filter(k => !DEDICATED_CONTROL_KEYS.has(k)).sort();
  const hasEdits = Object.keys(edits).length > 0;

  // Group settings by category (prefix before first _)
  const groups = {};
  for (const key of settingKeys) {
    const parts = key.split('_');
    // Use first two words as group label (e.g., AGENT_MAX → "Cost Controls", CHAT_RATE → "Rate Limiting")
    const category = categorize(key);
    if (!groups[category]) groups[category] = [];
    groups[category].push(key);
  }

  return (
    <div className="settings-page">
      <div className="settings-breadcrumb">
        <Link to="/cases">← Cases</Link>
      </div>
      <div className="page-header">
        <h1>Organization Settings</h1>
        {hasEdits && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : `Save ${Object.keys(edits).length} Change(s)`}
          </button>
        )}
      </div>

      <OrgMembersPanel currentUid={profile?.uid} />

      <OrgConsultantsPanel />

      <UsageBilling />

      <PrivacyAuditPanel orgId={profile?.organization_id} />

      {message && (
        <div className={`settings-message ${message.startsWith('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {Object.entries(groups).map(([group, keys]) => (
        <div key={group} className="settings-group">
          <h2 className="settings-group-title">{group}</h2>
          <table className="settings-table">
            <thead>
              <tr>
                <th>Setting</th>
                <th>Value</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(key => {
                const info = settings[key];
                const isEditing = key in edits;
                const currentValue = isEditing ? edits[key] : (info?.value ?? '');
                const source = info?.source || 'default';

                return (
                  <tr key={key} className={isEditing ? 'editing' : ''}>
                    <td className="setting-name">
                      <span className="setting-label">{LABELS[key] || key}</span>
                      <div className="setting-desc">{info?.description || ''}</div>
                    </td>
                    <td className="setting-value">
                      {info?.type === 'bool' ? (
                        <select
                          value={String(currentValue)}
                          onChange={e => handleEdit(key, e.target.value)}
                          className="input input-sm"
                        >
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : (
                        <input
                          type={info?.type === 'int' || info?.type === 'float' ? 'number' : 'text'}
                          value={currentValue}
                          onChange={e => handleEdit(key, e.target.value)}
                          className="input input-sm"
                          step={info?.type === 'float' ? '0.01' : undefined}
                        />
                      )}
                    </td>
                    <td className="setting-source">
                      <span className={`source-badge source-${source}`}>{source}</span>
                    </td>
                    <td className="setting-actions">
                      {isEditing && (
                        <button className="btn btn-sm btn-ghost" onClick={() => cancelEdit(key)}>
                          Cancel
                        </button>
                      )}
                      {source === 'org' && !isEditing && (
                        <button className="btn btn-sm btn-ghost" onClick={() => handleReset(key)} disabled={saving}>
                          Reset
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}


const DEDICATED_CONTROL_KEYS = new Set(['ORG_MONTHLY_SPEND_CEILING_USD']);

function categorize(key) {
  if (key.startsWith('AGENT_PRICING')) return 'Cost Controls';
  if (key.startsWith('AGENT_PREFLIGHT')) return 'Pre-flight Checks';
  if (key.startsWith('AGENT_THINKING') || key.startsWith('AGENT_CONTEXT')) return 'Agent Behavior';
  if (key.startsWith('CHAT_RATE')) return 'Rate Limiting';
  return 'General';
}

const LABELS = {
  AGENT_CONTEXT_WINDOW: 'Context Window',
  AGENT_THINKING_BUDGET: 'Thinking Budget',
  AGENT_PRICING_MODEL: 'Pricing Model',
  AGENT_PREFLIGHT_ENABLED: 'Pre-flight Checks Enabled',
  CHAT_RATE_LIMIT: 'Chat Rate Limit',
};
